import { Globe, RefreshCw, Database, AlertTriangle } from "lucide-react";
import { Button } from "./ui/button";
import React, { useState, useEffect } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/** Format an ISO string or Date as "DD MMM YYYY HH:MM UTC" */
function formatTimestamp(ts) {
  if (!ts) return "—";
  const d = ts instanceof Date ? ts : new Date(ts);
  if (isNaN(d.getTime())) return "—";
  return d.toUTCString().replace(/:\d{2} GMT$/, " UTC");
}

/** Returns true if the timestamp is more than 2 hours old (or missing). */
function isStale(ts) {
  if (!ts) return false;
  const d = new Date(ts);
  if (isNaN(d.getTime())) return false;
  return Date.now() - d.getTime() > 2 * 60 * 60 * 1000;
}

/** Format seconds as MM:SS */
function fmtCountdown(secs) {
  if (secs <= 0) return "now";
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return `${m}m ${String(s).padStart(2, "0")}s`;
}

const Header = ({ dataLastFetch, sourcesUsed = [], nextFetchIn, onRefresh }) => {
  const [internal, setInternal] = useState({ fetchedAt: null, sources: [], nextFetchTime: null });
  const [countdown, setCountdown] = useState(null);

  // Self-fetch /api/last-update on mount and every hour
  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/last-update`);
        const d = res.data;
        const fetchedAt = d.fetched_at ? new Date(d.fetched_at) : null;
        const nextFetchTime = fetchedAt ? new Date(fetchedAt.getTime() + 60 * 60 * 1000) : null;
        setInternal({
          fetchedAt,
          sources: d.sources || [],
          nextFetchTime,
        });
      } catch {
        // non-fatal
      }
    };
    load();
    const id = setInterval(load, 3_600_000);
    return () => clearInterval(id);
  }, []);

  // Live countdown ticker
  useEffect(() => {
    const tick = () => {
      const target = internal.nextFetchTime;
      if (!target) { setCountdown(null); return; }
      const secs = Math.max(0, Math.round((target - Date.now()) / 1000));
      setCountdown(secs);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [internal.nextFetchTime]);

  // Prefer passed props (Dashboard), fall back to self-fetched data
  const resolvedFetch = dataLastFetch ?? internal.fetchedAt;
  const resolvedSources = sourcesUsed.length > 0 ? sourcesUsed : internal.sources;
  // nextFetchIn prop is in minutes; convert to seconds for display
  const resolvedCountdown = nextFetchIn != null ? nextFetchIn * 60 : countdown;

  const stale = isStale(resolvedFetch);

  return (
    <header className="border-b border-zinc-800 bg-zinc-950/50 backdrop-blur-sm sticky top-0 z-50" data-testid="dashboard-header">
      <div className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          {/* Left: Logo + title */}
          <div className="flex items-center gap-4">
            <Globe className="w-8 h-8 text-red-500" />
            <div>
              <h1 className="text-2xl md:text-3xl font-bold uppercase tracking-tight heading-tactical" data-testid="app-title">
                Project WATCHTOWER
              </h1>
              <p className="text-xs text-zinc-500 font-mono uppercase tracking-wider">Global Conflict Monitoring</p>
              <p className="text-xs text-zinc-600 font-mono mt-0.5">
                Data courtesy of{" "}
                <a
                  href="https://ucdp.uu.se"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-zinc-400 hover:text-red-400 underline underline-offset-2 transition-colors"
                >
                  UCDP
                </a>
                {" "}— thank you for freely providing access.
              </p>
              <p className="text-xs text-zinc-600 font-mono mt-0.5">
                Built by{" "}
                <a
                  href="https://www.cybrain.tech/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-zinc-400 hover:text-red-400 underline underline-offset-2 transition-colors"
                >
                  cybrain.tech
                </a>
              </p>
            </div>
          </div>


          {/* Right: timestamps + buttons */}
          <div className="flex items-center gap-3">
            {/* Timestamp block — hidden on very small screens */}
            <div className="hidden sm:block text-right space-y-0.5 border-r border-zinc-800 pr-3 mr-1">
              <div data-testid="source-last-update">
                <p className="text-xs text-zinc-500 uppercase tracking-wider font-mono flex items-center justify-end gap-1">
                  <Database className="w-3 h-3" />
                  Sources updated
                </p>
                <p className="text-xs text-zinc-300 font-mono font-semibold" data-testid="source-update-time">
                  {formatTimestamp(resolvedFetch)}
                </p>
                {resolvedSources.length > 0 && (
                  <p className="text-xs text-zinc-600 font-mono">
                    {resolvedSources.join(" · ")}
                  </p>
                )}
                {resolvedCountdown != null && (
                  <p className="text-xs text-zinc-600 font-mono">
                    next in {fmtCountdown(resolvedCountdown)}
                  </p>
                )}
              </div>

              {/* Stale-data warning */}
              {resolvedFetch && stale && (
                <p className="flex items-center justify-end gap-1 text-xs font-mono text-amber-500 bg-amber-950/30 border border-amber-800/40 px-2 py-0.5 rounded-sm mt-1" data-testid="stale-warning">
                  <AlertTriangle className="w-3 h-3" />
                  Data may be stale
                </p>
              )}
            </div>

            <Button
              onClick={onRefresh}
              variant="secondary"
              size="sm"
              className="bg-zinc-800 hover:bg-zinc-700 text-zinc-300 font-mono text-xs px-3 py-1 rounded-sm border border-zinc-700"
              data-testid="refresh-btn"
            >
              <RefreshCw className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
